import { gql } from "apollo-boost";

import { QualifiedFeature, Flag } from "./shared";

const Reasoning = gql`
  fragment Reasoning on ClinicalReasoningFinding {
    id
    finding {
      ...QualifiedFeature
    }
    diseaseGivenFinding
    findingGivenDisease
    basedOnFindingGivenDisease
    absolutePredictiveValue
    diseaseIncidenceRate
    prevalence
    foundInPatient
    flags {
      ...Flag
    }
    noData
    redundantDueTo
    causality
    etgFindingGivenDisease
    etgDiseaseGivenFinding
    etgRiskFactor
    etgFindingGivenDiseaseBayes
    etiologySection
  }
  ${QualifiedFeature}
  ${Flag}
`;
const Publication = gql`
  fragment Publication on RuleRef {
    id
    chapter
    comments
    pageNumber
    publicationName
    url
  }
`;

const Path = gql`
  fragment Path on Path {
    source {
      ...QualifiedFeature
    }
    target {
      ...QualifiedFeature
    }
    edges {
      id
      source {
        ...QualifiedFeature
      }
      target {
        ...QualifiedFeature
      }
      prob
      lift
      type
      citations
    }
    prob
    probDiseaseGivenPath
    probFindingGivenDisease
    citations
  }
  ${QualifiedFeature}
`;

const ReasoningWithPath = gql`
  fragment ReasoningWithPath on ClinicalReasoningFinding {
    ...Reasoning
    paths {
      ...Path
    }
    citations
    graph {
      nodes {
        id
        qfs {
          ...QualifiedFeature
        }
        probability
        citations
      }
      edges {
        id
        sourceID
        targetID
        prob
        lift
        type
        citations
      }
    }
  }
  ${Reasoning}
  ${QualifiedFeature}
  ${Flag}
  ${Path}
`;

const FeatureAutoComplete = gql`
  fragment FeatureAutoComplete on Feature {
    name
    shortname
    alias
    hasRefinement
    id
    category {
      id
      name
      shortname
    }
    organSystem {
      id
      name
      shortname
    }
    qualifierSpec {
      relation {
        name
        id
        type
      }
      mandatory
    }
  }
`;

// TODO This fragment was copied from fe-common, because the gql-gen tool does not allow
//  import of gql fragments. Eventually all the queries should be moved to fe-common, which would solve this problem

export const MobileQueries = {
  FeaturesQuery: gql`
    query MFeaturesQuery($search: String, $filter: SearchFilter) {
      featureSearch(search: $search, limit: 50, filter: $filter) {
        ...FeatureAutoComplete
      }
    }
    ${FeatureAutoComplete}
  `,

  QualifiersQuery: gql`
    query MQualifiersQuery($feature: ID!, $relation: ID, $search: String) {
      feature(id: $feature) {
        qualifierSearch(search: $search, limit: 20, relation: $relation) {
          name
          relation {
            name
            id
            type
          }
          value {
            name
            shortname
            alias
            id
          }
        }
      }
    }
  `,

  QualifiersMultipleFeatures: gql`
    query MQualifiersMultipleQuery($features: [ID!], $search: String) {
      qualifierSearch(features: $features, limit: 25, search: $search) {
        name
        relation {
          name
          id
          type
        }
        value {
          name
          shortname
          alias
          id
        }
        feature {
          ...FeatureAutoComplete
        }
      }
    }
    ${FeatureAutoComplete}
  `,

  DiagnosisQuery: gql`
    query MDD(
      $qfs: [QualifiedFeatureInput]
      $depth: Int
      $limit: Int
      $pathOptions: PathOptions
    ) {
      dd(
        qualifiedFeatures: $qfs
        depth: $depth
        limit: $limit
        pathOptions: $pathOptions
      ) {
        clinicalReasoning {
          id
          disease {
            ...QualifiedFeature
          }
          prevalence
          isRedundant
          diseaseGivenDependentFindings
          diseaseGivenDependentFindingsUnNormalized
          liftWithDependentFindings
          flags {
            ...Flag
          }
          findings {
            finding {
              ...QualifiedFeature
            }
            diseaseGivenFinding
            findingGivenDisease
            basedOnFindingGivenDisease
            absolutePredictiveValue
            actualLiftInDD
            foundInPatient
            noData
          }
        }
        diseaseClusters {
          id
          crIds
          sortType
          title {
            ...QualifiedFeature
          }
          bestCrId
        }
        #        nextStep {
        #          ...NextStep
        #        }
      }
    }
    ${QualifiedFeature}
    ${Flag}
  `,

  DiseaseViewQuery: gql`
    query MDV($qf: QualifiedFeatureInput) {
      diseaseView(
        qualifiedFeature: $qf
        depth: 3
        limit: 100
        maxGroupHeight: 3
      ) {
        disease {
          ...QualifiedFeature
          feature {
            children(depth: 1) {
              features {
                name
                id
              }
            }
          }
        }
        references {
          ...Publication
        }
        prevalence
        categories {
          type
          findings {
            id
            complete
            parent {
              ...Reasoning
            }
            children
            type
            category
            maxFindingGivenDisease
            maxAbsolutePredictiveValue
          }
        }
        citations
      }
    }
    ${Reasoning}
    ${QualifiedFeature}
    ${Publication}
  `,

  DiseasePatientQuery: gql`
    query MPatientDV(
      $qf: QualifiedFeatureInput
      $patient: [QualifiedFeatureInput]
    ) {
      diseaseViewPatientOnly(disease: $qf, depth: 3, patient: $patient) {
        category
        clinicalReasoning {
          ...Reasoning
        }
      }
    }
    ${Reasoning}
    ${QualifiedFeature}
  `,

  DiseaseOverview: gql`
    query MDVOVerview($f: ID!) {
      feature(id: $f) {
        name
        organSystem {
          name
          shortname
        }

        children(depth: 1) {
          features {
            name
            id
          }
        }
        parents(depth: 1) {
          features {
            name
            id
          }
        }
        description {
          url
          text
        }
      }
    }
  `,

  PathsQuery: gql`
    query MCRF(
      $source: QualifiedFeatureInput
      $target: QualifiedFeatureInput
      $patient: [QualifiedFeatureInput]
    ) {
      clinicalReasoningFinding(
        disease: $source
        finding: $target
        patient: $patient
        depth: 3
      ) {
        ...ReasoningWithPath
      }
    }
    ${ReasoningWithPath}
  `,
  RestoreCaseQuery: gql`
    query MrestoreCase($findings: [QualifiedFeatureInput]) {
      qualifiedFeatures(qfs: $findings) {
        ...QualifiedFeature
      }
    }
    ${QualifiedFeature}
  `,

  FeedbackMutate: gql`
    mutation MFeedback($qfs: [QualifiedFeatureInput], $comment: String) {
      feedback(qualifiedFeatures: $qfs, comment: $comment)
    }
  `,
  PanelsQuery: gql`
    query Mpanels {
      defaultPanels {
        name
        id
        shortname
        organSystem {
          name
          shortname
        }
        category {
          id
          name
          shortname
        }
        findings {
          ...QualifiedFeature
        }
      }
    }
    ${QualifiedFeature}
  `,
};
